import { useLayoutEffect, useRef } from "react";
import styled from "styled-components";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import products from "../data/products";
import { useLight } from "../context/LightContext";
import LightSwitch from "../components/LightSwitch";
import ProductCard from "../components/ProductCard";

gsap.registerPlugin(ScrollTrigger);

export default function Home() {
  const { lit, isLit, toggleAll, toggleOne } = useLight();
  const rootRef = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      /* ---------- intro, runs once on load ---------- */
      const intro = gsap.timeline({
        defaults: { ease: "power3.out", duration: 1 },
      });

      intro
        .from("[data-intro='eyebrow']", { opacity: 0, y: 14, duration: 0.7 })
        .from(
          "[data-intro='line']",
          { opacity: 0, yPercent: 108, stagger: 0.09, duration: 1.05 },
          "-=0.35",
        )
        .from("[data-intro='lede']", { opacity: 0, y: 18 }, "-=0.7")
        .from(
          "[data-intro='switch']",
          { opacity: 0, y: 18, scale: 0.96, duration: 0.85 },
          "-=0.72",
        );

      /* ---------- staggered card reveals ---------- */
      gsap.set("[data-card]", { opacity: 0, y: 30 });

      ScrollTrigger.batch("[data-card]", {
        start: "top 92%",
        once: true,
        onEnter: (batch) =>
          gsap.to(batch, {
            opacity: 1,
            y: 0,
            duration: 0.85,
            ease: "power2.out",
            stagger: 0.07,
            overwrite: true,
          }),
      });

      /* ---------- section headers / closing block ---------- */
      gsap.utils.toArray("[data-reveal]").forEach((el) => {
        gsap.fromTo(
          el,
          { opacity: 0, y: 24 },
          {
            opacity: 1,
            y: 0,
            duration: 0.9,
            ease: "power2.out",
            scrollTrigger: { trigger: el, start: "top 90%", once: true },
          },
        );
      });
    }, rootRef);

    ScrollTrigger.refresh();
    return () => ctx.revert();
  }, []);

  return (
    <div ref={rootRef}>
      <Hero>
        <Eyebrow data-intro="eyebrow">Ndriçim · Prishtinë</Eyebrow>

        <Title>
          <Mask>
            <span data-intro="line">Shihe dritën</span>
          </Mask>
          <Mask>
            <em data-intro="line">para se ta blesh.</em>
          </Mask>
        </Title>

        <Lede data-intro="lede">
          Çdo llambë këtu mund ta ndezësh. Prek çelësin — ose vetëm atë që të
          intereson.
        </Lede>

        <SwitchRow data-intro="switch">
          <LightSwitch lit={lit} onToggle={toggleAll} />
        </SwitchRow>
      </Hero>

      <Section id="koleksioni">
        <SectionHead data-reveal>
          <SectionTitle>Koleksioni</SectionTitle>
          <HeadRight>
            <SectionNote>
              {products.length} modele · secila e provueshme
            </SectionNote>
            <LightSwitch lit={lit} onToggle={toggleAll} label={false} />
          </HeadRight>
        </SectionHead>

        <Grid>
          {products.map((p) => (
            <ProductCard
              key={p.id}
              product={p}
              lit={isLit(p.id)}
              onToggle={() => toggleOne(p.id)}
            />
          ))}
        </Grid>
      </Section>

      <Closing data-reveal>
        <ClosingTitle>
          Klienti që e sheh dritën
          <br />
          <em>nuk pyet “si duket?”</em>
        </ClosingTitle>
        <ClosingText>
          Rr. Nëna Terezë 14, Prishtinë · Hapur çdo ditë 10:00–21:00
        </ClosingText>
        <SwitchRow>
          <LightSwitch lit={lit} onToggle={toggleAll} />
        </SwitchRow>
      </Closing>
    </div>
  );
}

/* ------------------------------------------------------------------ */

const Hero = styled.section`
  padding: clamp(70px, 13vh, 150px) clamp(20px, 5vw, 64px)
    clamp(50px, 9vh, 100px);
  max-width: 1400px;
  margin: 0 auto;
`;

const Eyebrow = styled.div`
  font-size: 10px;
  letter-spacing: 0.4em;
  text-transform: uppercase;
  color: var(--ink-soft);
  margin-bottom: 26px;
`;

const Title = styled.h1`
  font-family: "Instrument Serif", Georgia, serif;
  font-weight: 400;
  font-size: clamp(36px, 11vw, 128px);
  line-height: 0.96;
  letter-spacing: -0.035em;

  em {
    font-style: italic;
    color: var(--ink-soft);
    transition: color var(--t-theme) var(--ease);
  }
`;

/* each headline line sits in its own clipping box so it can slide up
   from underneath instead of just fading in */
const Mask = styled.span`
  display: block;
  overflow: hidden;
  padding-bottom: 0.06em;

  > span,
  > em {
    display: block;
  }
`;

const Lede = styled.p`
  margin-top: 26px;
  max-width: 46ch;
  font-size: clamp(15px, 1.5vw, 18px);
  line-height: 1.6;
  color: var(--ink-soft);
`;

const SwitchRow = styled.div`
  margin-top: 40px;

  @media (max-width: 560px) {
    margin-top: 30px;
  }
`;

const Section = styled.section`
  padding: clamp(50px, 8vh, 90px) clamp(20px, 5vw, 64px);
  max-width: 1400px;
  margin: 0 auto;
`;

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding-bottom: 22px;
  margin-bottom: 40px;
  border-bottom: 1px solid var(--line);

  @media (max-width: 560px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
    padding-bottom: 16px;
    margin-bottom: 26px;
  }
`;

/* note + switch sit together on the right, so the whole grid can be
   flipped without leaving the collection in view */
const HeadRight = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(14px, 2vw, 26px);

  @media (max-width: 560px) {
    width: 100%;
    justify-content: space-between;
    gap: 12px;
  }
`;

const SectionTitle = styled.h2`
  font-family: "Instrument Serif", Georgia, serif;
  font-weight: 400;
  font-size: clamp(26px, 3.4vw, 42px);
  letter-spacing: -0.02em;
`;

const SectionNote = styled.span`
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--ink-soft);
  text-align: right;

  @media (max-width: 560px) {
    text-align: left;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: clamp(18px, 2.4vw, 40px) clamp(14px, 1.8vw, 28px);

  @media (max-width: 1080px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 780px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 26px 12px;
  }
`;

const Closing = styled.section`
  padding: clamp(80px, 15vh, 170px) clamp(20px, 5vw, 64px);
  max-width: 1400px;
  margin: 0 auto;
  border-top: 1px solid var(--line);
`;

const ClosingTitle = styled.h2`
  font-family: "Instrument Serif", Georgia, serif;
  font-weight: 400;
  font-size: clamp(27px, 6.6vw, 76px);
  line-height: 1.02;
  letter-spacing: -0.03em;

  em {
    font-style: italic;
    color: var(--ink-soft);
  }
`;

const ClosingText = styled.p`
  margin-top: 24px;
  font-size: 14px;
  color: var(--ink-soft);
`;
